import type { Lookups, WorkdayReport, WorkdayRow } from '@ft/shared';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLoad } from '../components/ui';
import { getLang, useI18n } from '../i18n';
import { getAreaId, getAreaName } from '../lib/area';
import { dfmt, eur2 } from '../lib/format';

const TONE = { new: 'info', differs: 'crit', unchanged: 'neu', unmatched: 'neg' } as const;
const FIELD_LABEL: Record<string, 'wd.col.date' | 'wd.col.amount' | 'wd.col.project'> = { invoice_date: 'wd.col.date', amount_cents: 'wd.col.amount', project_id: 'wd.col.project' };

/** Workday-Rechnungsexport hochladen, prüfen, Projekte zuordnen, übernehmen. */
export function WorkdayPage() {
  const { t } = useI18n();
  const { data: lookups } = useLoad<Lookups>('/api/lookups');
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<WorkdayReport | null>(null);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'check' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUnchanged, setShowUnchanged] = useState(false);

  async function run(apply: boolean, f = file) {
    if (!f) return;
    setBusy(apply ? 'apply' : 'check');
    setError(null);
    const body = new FormData();
    body.append('mappings', JSON.stringify(Object.fromEntries(Object.entries(mappings).filter(([, v]) => v))));
    body.append('file', f, f.name);
    try {
      const r = await fetch(`/api/invoices/workday${apply ? '?apply=1' : ''}`, { method: 'POST', body, headers: { 'x-lang': getLang(), ...(getAreaId() ? { 'x-area': getAreaId()! } : {}) } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.message ?? `Error ${r.status}`);
      setReport(j as WorkdayReport);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const pick = (f: File | undefined) => { if (!f) return; setFile(f); setReport(null); setMappings({}); void run(false, f); };
  const unmatchedKeys = useMemo(() => {
    const m = new Map<string, { text: string; n: number; cents: number }>();
    for (const r of report?.rows ?? []) if (r.state === 'unmatched') {
      const x = m.get(r.projectKey) ?? { text: r.projectText, n: 0, cents: 0 };
      m.set(r.projectKey, { ...x, n: x.n + 1, cents: x.cents + r.amountCents });
    }
    return [...m];
  }, [report]);
  const todo = report?.counts.new ?? 0;
  const rows = (report?.rows ?? []).filter((r) => showUnchanged || r.state !== 'unchanged');

  return (
    <section className="page" style={{ maxWidth: 1100 }}>
      <div className="crumbs"><Link to="/invoicing">{t('inv.title')}</Link><span>›</span><span>{t('wd.button')}</span></div>
      <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1 className="h1">{t('wd.title')}</h1><p className="muted lead">{t('wd.lead')}</p></div>

      <div className="box" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label className="drop" htmlFor="wdfile" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}>
          {busy === 'check' ? t('wd.checking') : file ? file.name : t('wd.drop')}
          <input id="wdfile" type="file" accept=".xlsx,.csv" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; pick(f); }} />
        </label>
        {error && <p className="err" role="alert">{error}</p>}
        {report && (
          <div className="row">
            {(['new', 'differs', 'unchanged', 'unmatched'] as const).map((s) => <span key={s} className={`pill ${TONE[s]}`}>{t(`wd.state.${s}`)}: {report.counts[s]}</span>)}
            <span className="spacer" />
            {!report.dryRun
              ? <span className="pill pos">{t('wd.applied', { new: report.counts.new })}</span>
              : todo > 0
                ? <button className="btn primary" disabled={!!busy} onClick={() => run(true)}>{busy === 'apply' ? t('wd.applying') : t('wd.apply', { n: todo })}</button>
                : <span className="muted">{t('wd.nothing')}</span>}
          </div>
        )}
        {report && <p className="muted small" style={{ margin: 0 }}>{t('wd.columns')}: {Object.entries(report.columns).map(([k, v]) => `${t(`wd.col.${k}` as 'wd.col.number')} = „${v}“`).join(' · ')}{report.skipped.length ? ` · ${t('wd.skipped', { n: report.skipped.length })}` : ''}</p>}
      </div>

      {report?.dryRun && unmatchedKeys.length > 0 && lookups && (
        <div className="box">
          <div className="panel-h"><h2>{t('wd.mapTitle')}</h2><span className="hint">{t('wd.mapLead')}</span></div>
          <div className="tablewrap"><table>
            <thead><tr><th>{t('wd.wdProject')}</th><th className="r">{t('wd.rows')}</th><th className="r">{t('wd.col.amount')}</th><th>{t('common.project')}</th></tr></thead>
            <tbody>
              {unmatchedKeys.map(([key, x]) => (
                <tr key={key}>
                  <td>{x.text}</td><td className="r num">{x.n}</td><td className="r num">{eur2(x.cents)}</td>
                  <td>
                    <select aria-label={`${t('common.project')}: ${x.text}`} value={mappings[key] ?? ''} onChange={(e) => setMappings((mm) => ({ ...mm, [key]: e.target.value }))}>
                      <option value="">{t('wd.mapSkip')}</option>
                      {lookups.customers.map((c) => (
                        <optgroup key={c.id} label={c.name}>
                          {lookups.projects.filter((p) => p.customerId === c.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="row" style={{ padding: '12px 18px' }}><span className="spacer" /><button className="btn" disabled={!!busy || !Object.values(mappings).some(Boolean)} onClick={() => run(false)}>{t('wd.recheck')}</button></div>
        </div>
      )}

      {report && (
        <div className="box">
          <div className="panel-h">
            <h2>{t('wd.rows')}</h2><span className="spacer" />
            <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={showUnchanged} onChange={(e) => setShowUnchanged(e.target.checked)} />{t('wd.showUnchanged')}</label>
          </div>
          <div className="tablewrap"><table>
            <thead><tr><th>{t('common.status')}</th><th>{t('wd.col.number')}</th><th>{t('wd.col.date')}</th><th>{t('common.project')}</th><th>{t('wd.col.po')}</th><th className="r">{t('wd.col.amount')}</th><th /></tr></thead>
            <tbody>
              {rows.map((r: WorkdayRow) => (
                <tr key={`${r.invoiceNumber}|${r.projectKey}`}>
                  <td><span className={`pill ${TONE[r.state]}`}>{t(`wd.state.${r.state}`)}</span></td>
                  <td className="mono">{r.invoiceNumber}</td>
                  <td className="num">{dfmt(r.invoiceDate)}</td>
                  <td>{r.project?.name ?? <span className="muted">{r.projectText}</span>}</td>
                  <td>{r.order ? <Link className="idlink" to={`/bestellungen/${r.order.id}`}>{r.order.orderNumber}</Link> : <span className="muted">{r.po ?? '–'}</span>}</td>
                  <td className="r num">{eur2(r.amountCents)}</td>
                  <td className="muted small">
                    {Object.entries(r.changes).map(([k, [a, b]]) => `${t(FIELD_LABEL[k] ?? 'wd.col.amount')}: ${k === 'amount_cents' ? `${eur2(Number(a))} → ${eur2(Number(b))}` : k === 'invoice_date' ? `${dfmt(String(a))} → ${dfmt(String(b))}` : `${lookups?.projects.find((x) => x.id === a)?.name ?? '?'} → ${lookups?.projects.find((x) => x.id === b)?.name ?? '?'}`}`).join(' · ')}
                    {r.notes.map((n) => t(`wd.note.${n}` as 'wd.note.no_date', { po: r.po ?? '' })).join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </section>
  );
}
